import { ArrowRight, Crown, X } from "lucide-react";
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";

export interface SubscriptionUpgradeReason {
  title: string;
  message: string;
  currentPlanName: string;
  targetPlanName: string;
  targetPlanCode: string | null;
}

export function SubscriptionUpgradeDialog({
  reason,
  close,
}: {
  reason: SubscriptionUpgradeReason;
  close: () => void;
}) {
  const dialog = useRef<HTMLElement>(null);

  useEffect(() => {
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const frame = window.requestAnimationFrame(() => dialog.current?.focus());
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
      if (event.key !== "Tab" || !dialog.current) return;
      const focusable = Array.from(
        dialog.current.querySelectorAll<HTMLElement>('button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'),
      );
      const first = focusable[0];
      const last = focusable.at(-1);
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus();
    };
  }, [close]);

  const upgradeTarget = reason.targetPlanCode
    ? `/tenant/subscription?upgrade=${encodeURIComponent(reason.targetPlanCode)}`
    : "/tenant/subscription";

  return createPortal(
    <div className="subscription-upgrade-layer">
      <button
        className="subscription-upgrade-layer__scrim"
        type="button"
        aria-label="Close plan message"
        onClick={close}
      />
      <section
        className="subscription-upgrade-dialog"
        ref={dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="subscription-upgrade-title"
        aria-describedby="subscription-upgrade-message"
        tabIndex={-1}
      >
        <button className="subscription-upgrade-dialog__close" type="button" aria-label="Close" onClick={close}>
          <X />
        </button>
        <span className="subscription-upgrade-dialog__icon" aria-hidden="true"><Crown /></span>
        <p className="eyebrow">PLAN UPGRADE</p>
        <h2 id="subscription-upgrade-title">{reason.title}</h2>
        <p id="subscription-upgrade-message">{reason.message}</p>
        <div className="subscription-upgrade-dialog__plans" aria-label="Plan upgrade path">
          <span><small>Current plan</small><strong>{reason.currentPlanName}</strong></span>
          <ArrowRight aria-hidden="true" />
          <span><small>Unlock with</small><strong>{reason.targetPlanName}</strong></span>
        </div>
        <div className="subscription-upgrade-dialog__actions">
          <button className="button button--quiet" type="button" onClick={close}>Not now</button>
          <Link className="button button--primary" to={upgradeTarget} onClick={close}>
            View upgrade <ArrowRight />
          </Link>
        </div>
      </section>
    </div>,
    document.body,
  );
}
